import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Input, PageHeader, Textarea, useToast } from "../components/ui";
import {
  blueprintService,
  type Blueprint,
  type WizardAnswers,
  type WizardQuestion,
} from "../services/blueprint.service";
import { validationErrors } from "../services/http";

/**
 * Segundo caminho de criação (além do chat): questionário → PRD → projeto.
 * As perguntas vêm do backend, que é a fonte única do questionário.
 */
export default function WizardPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [questions, setQuestions] = useState<WizardQuestion[]>([]);
  const [answers, setAnswers] = useState<WizardAnswers>({});
  const [step, setStep] = useState(0);
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null);
  const [projectName, setProjectName] = useState("");
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    blueprintService
      .questions()
      .then((list) => {
        setQuestions(list);
        const initial: WizardAnswers = {};
        list.forEach((question) => {
          initial[question.key] = question.type === "boolean" ? Boolean(question.default) : "";
        });
        setAnswers(initial);
      })
      .catch(() => toast.error("Não foi possível carregar o questionário."))
      .finally(() => setLoading(false));
  }, [toast]);

  const current = questions[step];
  const isLast = step === questions.length - 1;

  const canAdvance = useMemo(() => {
    if (!current) return false;
    if (current.type === "boolean") return true;
    return !current.required || String(answers[current.key] ?? "").trim().length > 0;
  }, [current, answers]);

  const setAnswer = (key: string, value: string | boolean) =>
    setAnswers((previous) => ({ ...previous, [key]: value }));

  const generate = async () => {
    setWorking(true);
    setErrors({});
    try {
      const result = await blueprintService.draft(answers);
      setBlueprint(result);
      setProjectName(result.title.slice(0, 80));
    } catch (error) {
      setErrors(validationErrors(error));
      toast.error("Não foi possível gerar o PRD.");
    } finally {
      setWorking(false);
    }
  };

  const createProject = async () => {
    if (!blueprint) return;
    setWorking(true);
    try {
      const { project } = await blueprintService.convert(blueprint.uuid, projectName.trim());
      toast.success("Projeto criado. O backlog foi gerado por etapas.");
      navigate(`/projects/${project.id}`);
    } catch (error) {
      setErrors(validationErrors(error));
      toast.error("Não foi possível criar o projeto.");
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-ink-muted">Carregando questionário…</p>;
  }

  if (blueprint) {
    const prd = blueprint.prd;

    return (
      <div className="space-y-6">
        <PageHeader
          title="Revise o que será criado"
          description={
            blueprint.generated_by_ai
              ? "PRD gerado pela IA a partir das suas respostas."
              : "PRD gerado a partir das suas respostas (nenhum provider de IA respondeu)."
          }
        />

        <Card>
          <p className="text-base text-ink">{prd.summary}</p>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <PrdBlock title="Entidades principais" items={prd.entities.map((e) => `${e.name}${e.description ? ` — ${e.description}` : ""}`)} />
          <PrdBlock title="Módulos" items={prd.modules.map((m) => `${m.name}${m.description ? ` — ${m.description}` : ""}`)} />
          <PrdBlock title="Perfis" items={prd.profiles.map((p) => `${p.name}${p.permissions?.length ? ` — ${p.permissions.join(", ")}` : ""}`)} />
          <PrdBlock title="Fluxos" items={prd.flows.map((f) => `${f.name}${f.steps?.length ? ` — ${f.steps.join(" → ")}` : ""}`)} />
        </div>

        <Card>
          <div className="space-y-4">
            <Input
              label="Nome do projeto"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              error={errors.name?.[0]}
            />
            <div className="flex flex-wrap gap-3">
              <Button onClick={createProject} disabled={working || projectName.trim() === ""}>
                {working ? "Criando…" : "Criar projeto"}
              </Button>
              <Button variant="ghost" onClick={() => setBlueprint(null)} disabled={working}>
                Ajustar respostas
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Criar sistema com IA"
        description={`Pergunta ${step + 1} de ${questions.length}`}
      />

      <Card>
        {current && (
          <div className="space-y-4">
            <p className="text-lg font-medium text-ink">{current.label}</p>

            {current.type === "textarea" && (
              <Textarea
                rows={6}
                value={String(answers[current.key] ?? "")}
                placeholder={current.placeholder}
                onChange={(event) => setAnswer(current.key, event.target.value)}
              />
            )}

            {current.type === "text" && (
              <Input
                value={String(answers[current.key] ?? "")}
                placeholder={current.placeholder}
                onChange={(event) => setAnswer(current.key, event.target.value)}
              />
            )}

            {current.type === "boolean" && (
              <div className="flex gap-3">
                <Button
                  variant={answers[current.key] ? "primary" : "ghost"}
                  onClick={() => setAnswer(current.key, true)}
                >
                  Sim
                </Button>
                <Button
                  variant={answers[current.key] ? "ghost" : "primary"}
                  onClick={() => setAnswer(current.key, false)}
                >
                  Não
                </Button>
              </div>
            )}

            <div className="flex flex-wrap justify-between gap-3 pt-2">
              <Button variant="ghost" onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={step === 0}>
                Voltar
              </Button>
              {isLast ? (
                <Button onClick={generate} disabled={!canAdvance || working}>
                  {working ? "Gerando PRD…" : "Gerar PRD"}
                </Button>
              ) : (
                <Button onClick={() => setStep((value) => value + 1)} disabled={!canAdvance}>
                  Continuar
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function PrdBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <Card title={title}>
      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">Nada identificado.</p>
      ) : (
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}
